import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getReport, publishReport, updateReport } from '../services/generatedReportService';

const SENTIMENT_COLORS = { positive: '#2e7d32', neutral: '#555', negative: '#c62828', mixed: '#e65100' };

const ReportDetailPage = () => {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    getReport(id)
      .then((res) => {
        setReport(res.data);
        setSummaryDraft(res.data.content?.summary || '');
      })
      .catch(() => setMsg('Report not found.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await publishReport(id);
      setReport(res.data);
      setMsg('Report published.');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const handleSaveSummary = async () => {
    try {
      const res = await updateReport(id, { content: { ...report.content, summary: summaryDraft } });
      setReport(res.data);
      setEditingSummary(false);
      setMsg('Summary updated.');
    } catch (err) {
      setMsg('Failed to update summary');
    }
  };

  if (loading) return <p className="page-loading">Loading…</p>;
  if (!report) return <p className="page-error">{msg || 'Not found.'}</p>;

  const { content } = report;

  return (
    <div className="page report-detail-page">
        <div className="report-header">
          <div>
            <h2>{report.title}</h2>
            <span className={`status-badge status-${report.status}`}>{report.status}</span>
            <span className="badge">{report.type.replace('_', ' ')}</span>
          </div>
          <div className="report-actions">
            {report.status !== 'published' && (
              <button className="btn" onClick={handlePublish} disabled={publishing}>
                {publishing ? 'Publishing...' : 'Publish Report'}
              </button>
            )}
            <Link to="/reports">← Back to Reports</Link>
          </div>
        </div>

        {msg && <p className="success-msg">{msg}</p>}

        <div className="report-meta">
          <span>Generated: {new Date(report.createdAt).toLocaleDateString()}</span>
          {report.publishedAt && <span>Published: {new Date(report.publishedAt).toLocaleDateString()}</span>}
          {report.generatedBy && <span>By: {report.generatedBy.firstName || report.generatedBy.email}</span>}
        </div>

        {/* Summary */}
        <section className="report-section">
          <div className="section-header">
            <h3>Summary</h3>
            {report.status !== 'published' && (
              <button className="btn-sm btn-secondary" onClick={() => setEditingSummary(!editingSummary)}>
                {editingSummary ? 'Cancel' : 'Edit'}
              </button>
            )}
          </div>
          {editingSummary ? (
            <div>
              <textarea
                value={summaryDraft}
                onChange={(e) => setSummaryDraft(e.target.value)}
                rows={5}
                style={{ width: '100%' }}
              />
              <button className="btn" onClick={handleSaveSummary}>Save Summary</button>
            </div>
          ) : (
            <p>{content?.summary || 'No summary available.'}</p>
          )}
        </section>

        {/* Stats */}
        <section className="report-section stats-row">
          <div className="stat-card">
            <div className="stat-number">{content?.totalResponses ?? 0}</div>
            <div className="stat-label">Total Responses</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{content?.assessmentIds?.length ?? 0}</div>
            <div className="stat-label">Assessments</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{content?.concerns?.length ?? 0}</div>
            <div className="stat-label">Concerns Identified</div>
          </div>
        </section>

        {/* Key Findings */}
        {content?.keyFindings?.length > 0 && (
          <section className="report-section">
            <h3>Key Findings</h3>
            <ol className="findings-list">
              {content.keyFindings.map((f, i) => <li key={i}>{f}</li>)}
            </ol>
          </section>
        )}

        {/* Concerns Table */}
        {content?.concerns?.length > 0 && (
          <section className="report-section">
            <h3>Concerns</h3>
            <table className="report-table">
              <thead>
                <tr><th>Concern</th><th>Category</th><th>Score</th><th>Sentiment</th></tr>
              </thead>
              <tbody>
                {content.concerns.map((c, i) => (
                  <tr key={i}>
                    <td>{c.label}</td>
                    <td><span className={`category-pill cat-${c.category}`}>{c.category}</span></td>
                    <td>
                      <div className="score-bar-track">
                        <div className="score-bar-fill" style={{ width: `${(c.score / 10) * 100}%` }} />
                        <span>{c.score}/10</span>
                      </div>
                    </td>
                    <td style={{ color: SENTIMENT_COLORS[c.sentiment] || '#555' }}>{c.sentiment || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Segment Breakdown */}
        {content?.segmentBreakdown?.length > 0 && (
          <section className="report-section">
            <h3>Response Segments</h3>
            <div className="segment-bars">
              {content.segmentBreakdown.map((s) => (
                <div key={s.segment} className="segment-bar-row">
                  <span className="segment-label">{s.segment.replace(/_/g, ' ')}</span>
                  <div className="segment-bar-track">
                    <div className={`segment-bar fill-${s.segment}`} style={{ width: `${s.percentage}%` }} />
                  </div>
                  <span className="segment-count">{s.count} ({s.percentage}%)</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Recommendations */}
        {content?.recommendations?.length > 0 && (
          <section className="report-section">
            <h3>Recommendations</h3>
            <ul className="recommendations-list">
              {content.recommendations.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </section>
        )}
      </div>
  );
};

export default ReportDetailPage;
